// frontend/src/components/SurveyDisplay.js
/**
 * Survey Display Component
 * Shows surveys to users in a non-intrusive, elegant way with theme support
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Send,
  Star,
  CheckCircle,
  MessageSquare,
  HelpCircle,
  ThumbsUp,
  AlertTriangle
} from 'lucide-react';
import axios from '../axiosConfig';
import { toast } from 'react-toastify';
import { ThemedButton, ThemedCard } from './themed';
import { useTheme } from '../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';
import logger from '../utils/logger';

const isQuestionRequired = (question) => (
    question?.is_required === true ||
    question?.is_required === 1 ||
    question?.is_required === '1'
);

const isMissingSurveyResponseValue = (value) => (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
);

const SurveyDisplay = ({ 
    survey, 
    entityId, 
    tripId = null, 
    bookingId = null,
    onCancel,
    onClose, 
    onComplete,
    submitResponses,
    displayDelay = 5 // seconds before showing survey
}) => {
    const { t } = useTranslation();
    const { text, border, components } = useTheme();
    
    // State management
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [responses, setResponses] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [questionStartTime, setQuestionStartTime] = useState(Date.now());
    const [isVisible, setIsVisible] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [showSkipConfirm, setShowSkipConfirm] = useState(false);

    // Get current question
    const currentQuestion = survey?.questions?.[currentQuestionIndex];
    const totalQuestions = survey?.questions?.length || 0;
    const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;

    
    // Auto-show survey after delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, displayDelay * 1000);

        return () => clearTimeout(timer);
    }, [displayDelay]);

    // Track question timing
    useEffect(() => {
        setQuestionStartTime(Date.now());
    }, [currentQuestionIndex]);

    // Handle response change
    const handleResponseChange = (value) => {
        const responseTime = Math.floor((Date.now() - questionStartTime) / 1000);
        
        setResponses({
            ...responses,
            [currentQuestion.id]: {
                question_id: currentQuestion.id,
                response_value: value,
                response_type: currentQuestion.question_type,
                response_time: responseTime
            }
        });
    };

    // Navigate to next question
    const handleNext = () => {
        // Required validation must distinguish deliberate falsy answers such
        // as "No" from genuinely unanswered questions.
        const responseValue = responses[currentQuestion.id]?.response_value;
        if (isQuestionRequired(currentQuestion) && isMissingSurveyResponseValue(responseValue)) {
            toast.warning(t('This question is required'));
            return;
        }

        if (currentQuestionIndex < totalQuestions - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            // Last question - submit survey
            handleSubmit();
        }
    };

    // Navigate to previous question
    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    // Skip survey
    const handleSkip = () => {
        // Use themed confirmation dialog so the experience stays consistent with app styling
        setShowSkipConfirm(true);
    };

    const handleConfirmSkip = () => {
        setShowSkipConfirm(false);
        if (onCancel) {
            onCancel();
        } else if (onClose) {
            onClose();
        } else {
            setIsVisible(false);
        }
    };

    const handleCancelSkip = () => {
        setShowSkipConfirm(false);
    };

    // Submit survey responses
    const handleSubmit = async () => {
        setIsSubmitting(true);

        // Prepare responses array
        const responsesArray = Object.values(responses);

        // Add unanswered questions as empty responses
        survey.questions.forEach(question => {
            if (!responses[question.id]) {
                responsesArray.push({
                    question_id: question.id,
                    response_value: null,
                    response_type: question.question_type,
                    response_time: 0
                });
            }
        });

        try {
            // Token-link survey pages provide their own submitter for the
            // public token endpoint. Authenticated in-app surveys keep the
            // original entity-scoped endpoint and metadata payload.
            const submitResult = submitResponses
                ? await submitResponses(responsesArray)
                : await axios.post(`/api/surveys/${entityId}/response`, {
                    surveyId: survey.id,
                    responses: responsesArray,
                    tripId,
                    bookingId,
                    deviceType: getDeviceType()
                });

            if (submitResult === false) {
                return;
            }

            setShowThankYou(true);
            
            // Auto-close after showing thank you
            setTimeout(() => {
                if (onComplete) {
                    onComplete(responsesArray);
                }
                if (onClose) {
                    onClose();
                }
            }, 3000);

        } catch (error) {
            logger.error('Error submitting survey', error);
            toast.error(t('Failed to submit survey. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get device type
    const getDeviceType = () => {
        const width = window.innerWidth;
        if (width < 768) return 'mobile';
        if (width < 1024) return 'tablet';
        return 'desktop';
    };

    // Render question based on type
    const renderQuestion = () => {
        if (!currentQuestion) return null;

        const response = responses[currentQuestion.id]?.response_value;

        switch (currentQuestion.question_type) {
            case 'rating':
            case 'scale':
            case 'scale_1_5':
                return (
                    <div className="flex justify-center gap-2">
                        {[1, 2, 3, 4, 5].map(value => {
                            const isFilled = response >= value;
                            const isCurrentValue = response === value;
                            const optionAriaLabel = t('survey.scaleOptionAriaLabel', { value });

                            return (
                                <button
                                    key={value}
                                    aria-label={optionAriaLabel}
                                    title={optionAriaLabel}
                                    aria-pressed={isFilled}
                                    onClick={() => handleResponseChange(value)}
                                    className={`
                                        w-12 h-12 rounded-lg border flex items-center justify-center transition-all
                                        ${isFilled
                                            ? 'bg-blue-50 border-blue-200 text-blue-600'
                                            : `${components.card} ${border.light} text-gray-400 hover:${border.focus}`
                                        }
                                        ${isCurrentValue ? 'ring-2 ring-blue-500 ring-offset-2 scale-105' : ''}
                                    `}
                                >
                                    <Star className={`w-6 h-6 ${isFilled ? 'fill-current' : ''}`} />
                                </button>
                            );
                        })}
                    </div>
                );

            case 'scale_1_10':
                return (
                    <div className="grid grid-cols-5 gap-2">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(value => {
                            const optionLabel = t('survey.scaleOptionLabel', { value });
                            const optionAriaLabel = t('survey.scaleOptionAriaLabel', { value });

                            return (
                                <button
                                    key={value}
                                    aria-label={optionAriaLabel}
                                    title={optionAriaLabel}
                                    onClick={() => handleResponseChange(value)}
                                    className={`
                                        p-3 rounded-lg transition-all
                                        ${response === value 
                                            ? 'bg-blue-500 text-white' 
                                            : `${components.card} ${border.light} hover:${border.focus}`
                                        }
                                    `}
                                >
                                    {optionLabel}
                                </button>
                            );
                        })}
                    </div>
                );

            case 'yes_no':
                return (
                    <div className="flex justify-center gap-4">
                        <button
                            onClick={() => handleResponseChange(true)}
                            className={`
                                px-8 py-3 rounded-lg transition-all flex items-center gap-2
                                ${response === true 
                                    ? 'bg-green-500 text-white' 
                                    : `${components.card} ${border.light} hover:${border.focus}`
                                }
                            `}
                        >
                            <CheckCircle className="w-5 h-5" />
                            {t('Yes')}
                        </button>
                        <button
                            onClick={() => handleResponseChange(false)}
                            className={`
                                px-8 py-3 rounded-lg transition-all flex items-center gap-2
                                ${response === false 
                                    ? 'bg-red-500 text-white' 
                                    : `${components.card} ${border.light} hover:${border.focus}`
                                }
                            `}
                        >
                            <X className="w-5 h-5" />
                            {t('No')}
                        </button>
                    </div>
                );

            case 'multiple_choice':
                return (
                    <div className="space-y-2">
                        {currentQuestion.options?.map(option => (
                            <button
                                key={option.id}
                                onClick={() => handleResponseChange(option.id)}
                                className={`
                                    w-full p-3 rounded-lg text-left transition-all flex items-center gap-3
                                    ${response === option.id 
                                        ? 'bg-blue-500 text-white' 
                                        : `${components.card} ${border.light} hover:${border.focus}`
                                    }
                                `}
                            >
                                <div className={`
                                    w-5 h-5 rounded-full border-2 flex items-center justify-center
                                    ${response === option.id ? 'border-white' : `${border.light}`}
                                `}>
                                    {response === option.id && (
                                        <div className="w-3 h-3 rounded-full bg-white" />
                                    )}
                                </div>
                                {option.option_text}
                            </button>
                        ))}
                    </div>
                );

            case 'checkbox': {
                const selectedOptions = response || [];
                return (
                    <div className="space-y-2">
                        {currentQuestion.options?.map(option => {
                            const isSelected = selectedOptions.includes(option.id);
                            return (
                                <button
                                    key={option.id}
                                    onClick={() => {
                                        const newSelection = isSelected
                                            ? selectedOptions.filter(id => id !== option.id)
                                            : [...selectedOptions, option.id];
                                        handleResponseChange(newSelection);
                                    }}
                                    className={`
                                        w-full p-3 rounded-lg text-left transition-all flex items-center gap-3
                                        ${isSelected 
                                            ? 'bg-blue-500 text-white' 
                                            : `${components.card} ${border.light} hover:${border.focus}`
                                        }
                                    `}
                                >
                                    <div className={`
                                        w-5 h-5 rounded border-2 flex items-center justify-center
                                        ${isSelected ? 'border-white' : `${border.light}`}
                                    `}>
                                        {isSelected && <CheckCircle className="w-4 h-4" />}
                                    </div>
                                    {option.option_text}
                                </button>
                            );
                        })}
                    </div>
                );
            }

            case 'text':
                return (
                    <textarea
                        value={response || ''}
                        onChange={(e) => handleResponseChange(e.target.value)}
                        placeholder={t('Type your response here...')}
                        className={`
                            w-full p-3 rounded-lg resize-none
                            ${components.input} ${border.light} focus:${border.focus}
                        `}
                        rows="4"
                    />
                );

            default:
                return null;
        }
    };

    // Don't render if not visible
    if (!isVisible || !survey) return null;

    // Keep the confirmation dialog ready so it renders alongside any survey view state (expanded, minimized, thank you)
    const skipConfirmationDialog = (
        <ConfirmDialogShell
            isOpen={showSkipConfirm}
            onClose={handleCancelSkip}
            title={t('Skip Survey')}
            onConfirm={handleConfirmSkip}
            confirmLabel={t('Skip Survey')}
            cancelLabel={t('Continue Survey')}
            confirmVariant="warning"
            confirmIcon={AlertTriangle}
            showIcon
            iconVariant="warning"
        >
            <p className={`${text.secondary} text-sm`}>
                {t('Are you sure you want to skip this survey?')}
            </p>
        </ConfirmDialogShell>
    );

    // Thank you screen
    if (showThankYou) {
        return (
            <>
                {skipConfirmationDialog}
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="fixed bottom-4 right-4 z-50"
                    >
                        <ThemedCard className="p-6 max-w-md">
                            <div className="text-center">
                                <ThumbsUp className="w-12 h-12 mx-auto mb-3 text-green-500" />
                                <h3 className="text-xl font-semibold mb-2">{t('Thank You!')}</h3>
                                <p className={text.secondary}>
                                    {t('Your feedback has been submitted successfully.')}
                                </p>
                            </div>
                        </ThemedCard>
                    </motion.div>
                </AnimatePresence>
            </>
        );
    }

    // Minimized view
    if (isMinimized) {
        return (
            <>
                {skipConfirmationDialog}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="fixed bottom-4 right-4 z-50"
                >
                    <ThemedButton
                        onClick={() => setIsMinimized(false)}
                        className="shadow-lg"
                    >
                        <MessageSquare className="w-5 h-5 mr-2" />
                        {t('Continue Survey')}
                    </ThemedButton>
                </motion.div>
            </>
        );
    }

    // Main survey display
    return (
        <>
            {skipConfirmationDialog}
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="fixed bottom-4 right-4 z-50 max-w-lg w-full sm:w-96"
                >
                    <ThemedCard className="p-0 shadow-2xl">
                        {/* Header */}
                        <div className={`p-4 border-b ${border.light}`}>
                            <div className="flex justify-between items-center">
                                <h3 className="font-semibold">
                                    {survey.name || (
                                        survey.survey_type === 'service'
                                            ? t('serviceQualitySurvey')
                                            : t('systemFeedbackSurvey')
                                    )}
                                </h3>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setIsMinimized(true)}
                                        className={`p-1 rounded hover:${components.hover} transition-colors`}
                                        title={t('Minimize')}
                                    >
                                        <ChevronDown className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={handleSkip}
                                        className={`p-1 rounded hover:${components.hover} transition-colors`}
                                        title={t('Close')}
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            
                            {/* Progress bar */}
                            <div className="mt-3">
                                <div className={`h-2 rounded-full ${components.card}`}>
                                    <div 
                                        className="h-full rounded-full bg-blue-500 transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <p className={`text-xs mt-1 ${text.secondary}`}>
                                    {t('survey.questionProgress', {
                                        current: currentQuestionIndex + 1,
                                        total: totalQuestions
                                    })}
                                </p>
                            </div>
                        </div>

                        {/* Question Content */}
                        <div className="p-6">
                            <div className="mb-4">
                                <p className="font-medium mb-2">
                                    {currentQuestion?.question_text}
                                    {isQuestionRequired(currentQuestion) && (
                                        <span className="text-red-500 ml-1">*</span>
                                    )}
                                </p>
                                {currentQuestion?.help_text && (
                                    <p className={`text-sm ${text.secondary} flex items-start gap-1`}>
                                        <HelpCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                        {currentQuestion.help_text}
                                    </p>
                                )}
                            </div>

                            {/* Question Input */}
                            <div className="mb-6">
                                {renderQuestion()}
                            </div>

                            {/* Navigation */}
                            <div className="flex justify-between items-center">
                                <ThemedButton
                                    onClick={handlePrevious}
                                    variant="ghost"
                                    disabled={currentQuestionIndex === 0}
                                    className="flex items-center gap-1"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    {t('Previous')}
                                </ThemedButton>

                                <span className={`text-sm ${text.secondary}`}>
                                    {t('survey.questionProgressCompact', {
                                        current: currentQuestionIndex + 1,
                                        total: totalQuestions
                                    })}
                                </span>

                                <ThemedButton
                                    onClick={handleNext}
                                    disabled={isSubmitting}
                                    className="flex items-center gap-1"
                                >
                                    {currentQuestionIndex === totalQuestions - 1 ? (
                                        <>
                                            <Send className="w-4 h-4" />
                                            {isSubmitting ? t('Submitting...') : t('Submit')}
                                        </>
                                    ) : (
                                        <>
                                            {t('Next')}
                                            <ChevronRight className="w-4 h-4" />
                                        </>
                                    )}
                                </ThemedButton>
                            </div>
                        </div>
                    </ThemedCard>
                </motion.div>
            </AnimatePresence>
        </>
    );
};

export default SurveyDisplay;
